import { DestroyRef, Injectable, inject, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of, shareReplay } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { I18N_PREFIX, TranslationModule, translationFileName } from './i18n.config';

export type TranslationObject = Record<string, any>;

/**
 * HTTP-завантажувач окремих файлів локалей.
 *
 * Відповідає рівно за одну річ: дістати `assets/i18n/<lang>[.<module>].json`
 * і не зробити цього двічі. Кеш тримається за ключем `<lang>|<module>`, тож
 * повторний запит того самого файлу (навіть поки перший ще в польоті)
 * повертає той самий стрім без нового HTTP-виклику.
 */
@Injectable({ providedIn: 'root' })
export class TranslationLoaderService {
  private readonly http = inject(HttpClient);

  /** `<lang>|<module>` -> стрім із вмістом файлу (shareReplay = кеш + дедуплікація). */
  private readonly cache = new Map<string, Observable<TranslationObject>>();

  /** Файли, які вже реально доїхали (для синхронної перевірки). */
  private readonly resolved = new Set<string>();

  public load(language: string, module?: TranslationModule | null): Observable<TranslationObject> {
    const cacheKey = this.cacheKey(language, module);
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const url = `${I18N_PREFIX}${translationFileName(language, module)}`;
    const request$ = this.http.get<TranslationObject>(url).pipe(
      map((translations) => translations ?? {}),
      catchError(() => {
        // Відсутній файл модуля — не помилка: модуль просто не має власних перекладів
        // для цієї мови. Кешуємо порожній результат, щоб не довбати 404 на кожен ключ.
        return of({} as TranslationObject);
      }),
      map((translations) => {
        this.resolved.add(cacheKey);
        return translations;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.cache.set(cacheKey, request$);
    return request$;
  }

  /** Паралельно вантажить кілька модулів і зливає їх в один об'єкт. */
  public loadMany(language: string, modules: readonly TranslationModule[]): Observable<TranslationObject> {
    if (!modules.length) {
      return of({});
    }
    return forkJoin(modules.map((module) => this.load(language, module))).pipe(
      map((chunks) => chunks.reduce<TranslationObject>((acc, chunk) => ({ ...acc, ...chunk }), {})),
    );
  }

  public isLoaded(language: string, module?: TranslationModule | null): boolean {
    return this.resolved.has(this.cacheKey(language, module));
  }

  private cacheKey(language: string, module?: TranslationModule | null): string {
    return `${language}|${module ?? '__base__'}`;
  }
}

/**
 * `TranslateLoader` для базового словника `assets/i18n/<lang>.json`.
 *
 * Делегує в `TranslationLoaderService`, тож база і модулі ходять через один кеш.
 *
 * Додатково слугує сигналізацією: лоадер створюється рівно один раз на інжектор,
 * у якому провайдиться i18n. Другий інстанс означає, що `provideI18n()` потрапив
 * не тільки в `AppModule` (наприклад, у `SharedModule`, який імпортують ліниві
 * модулі) — а це рівно той баг, через який зміна мови не працює без reload:
 * кожен лінивий модуль отримує власний `TranslateService`/`TranslateStore`.
 */
@Injectable()
export class BaseTranslateLoader implements TranslateLoader {
  private static instances = 0;

  private readonly loader = inject(TranslationLoaderService);

  constructor() {
    // Рахуємо саме ОДНОЧАСНО живі інстанси: у тестах інжектор перестворюється
    // на кожен spec, і це нормально — проблема лише коли їх кілька водночас.
    BaseTranslateLoader.instances += 1;
    inject(DestroyRef).onDestroy(() => {
      BaseTranslateLoader.instances -= 1;
    });

    if (BaseTranslateLoader.instances > 1 && isDevMode()) {
      console.error(
        '[i18n] Створено %d-й TranslateLoader. provideI18n() має викликатись ЛИШЕ в AppModule — ' +
          'інакше кожен лінивий модуль дістає власний TranslateService і мова не перемикається без ' +
          'перезавантаження сторінки. Деталі: docs/I18N.md',
        BaseTranslateLoader.instances,
      );
    }
  }

  public getTranslation(language: string): Observable<TranslationObject> {
    return this.loader.load(language);
  }
}
